import { demoCredentials } from "@qualtio-workhub/auth/demo";
import { Avatar, Button, Card, Input } from "@qualtio-workhub/ui";
import { Navigate } from "@tanstack/react-router";
import { LogOut, Moon, Search, Sun } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { getAuthConfig, getGoogleLoginUrl } from "../shared/api/client";
import { useOptionalWorkhubQuery } from "../shared/api/hooks";
import { useAuth } from "../shared/auth/auth-provider";
import { useI18n } from "../shared/i18n/provider";
import { BrandMark } from "./brand";

export function LoginPage() {
  const auth = useAuth();
  const { t } = useI18n();
  const authConfig = useOptionalWorkhubQuery(getAuthConfig);
  const mode = authConfig.data?.authMode;
  const [email, setEmail] = useState<string>(demoCredentials.email);
  const [password, setPassword] = useState<string>(demoCredentials.password);
  const [error, setError] = useState(false);

  if (auth.status === "authenticated") {
    return <Navigate to="/my-work" />;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(false);

    try {
      await auth.login(email, password);
    } catch {
      setError(true);
    }
  }

  return (
    <main className="login-page">
      <Card className="login-panel">
        <div className="brand-lockup">
          <BrandMark />
          <div>
            <h1 className="login-title">{t("auth.title")}</h1>
            <p className="login-subtitle">{t("auth.subtitle")}</p>
          </div>
        </div>
        {mode === undefined ? (
          <p className="muted-copy">{t("auth.loading")}</p>
        ) : mode === "google" ? (
          <div className="login-form">
            <Button asChild>
              <a href={getGoogleLoginUrl()}>{t("auth.googleLogin")}</a>
            </Button>
            <p className="muted-copy">{t("auth.googleHint")}</p>
          </div>
        ) : (
          <form className="login-form" onSubmit={onSubmit}>
            <label className="field" htmlFor="email">
              <span>{t("auth.email")}</span>
              <Input
                autoComplete="email"
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.currentTarget.value)}
              />
            </label>
            <label className="field" htmlFor="password">
              <span>{t("auth.password")}</span>
              <Input
                autoComplete="current-password"
                id="password"
                name="password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.currentTarget.value)}
              />
            </label>
            {error ? <p className="error-text">{t("auth.error")}</p> : null}
            <Button type="submit">{t("auth.login")}</Button>
            <p className="muted-copy">{t("auth.demoHint")}</p>
          </form>
        )}
      </Card>
    </main>
  );
}

export function LoadingPage() {
  const { t } = useI18n();
  return <div className="app-loading">{t("app.loading")}</div>;
}

export function TopbarControls({
  onCommandOpen,
  onThemeToggle,
  isDark,
}: {
  onCommandOpen: () => void;
  onThemeToggle: () => void;
  isDark: boolean;
}) {
  const auth = useAuth();
  const { locale, setLocale, t } = useI18n();

  return (
    <div className="topbar-actions">
      <Button
        aria-label={t("search.global")}
        aria-keyshortcuts="Control+K Meta+K"
        className="search-trigger"
        variant="secondary"
        onClick={onCommandOpen}
      >
        <Search aria-hidden="true" size={16} />
        <span>{t("search.global")}</span>
        <kbd className="search-shortcut">{t("command.shortcut")}</kbd>
      </Button>
      <Button aria-label={t("common.theme")} size="icon" variant="ghost" onClick={onThemeToggle}>
        {isDark ? <Sun size={16} /> : <Moon size={16} />}
      </Button>
      <Button
        aria-label={`${t("common.language")} ${locale.toUpperCase()}`}
        size="sm"
        variant="ghost"
        onClick={() => setLocale(locale === "en" ? "es" : "en")}
      >
        {locale.toUpperCase()}
      </Button>
      <Avatar>{auth.user?.name.slice(0, 1) ?? "Q"}</Avatar>
      <Button
        aria-label={t("auth.logout")}
        size="icon"
        variant="ghost"
        onClick={() => void auth.logout()}
      >
        <LogOut aria-hidden="true" size={16} />
      </Button>
    </div>
  );
}

export function useCommandPaletteShortcut(onOpen: () => void) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        onOpen();
      }
    };
    const onCustomOpen = () => onOpen();
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("workhub:open-command", onCustomOpen);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("workhub:open-command", onCustomOpen);
    };
  }, [onOpen]);
}
